import React, { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus, Mail, Phone, MapPin, Building2, User as UserIcon, Star, MoreVertical,
  Pencil, Trash2, Upload, AlertTriangle,
} from "lucide-react";

const EMPTY = {
  client_type: "individual", display_name: "", legal_name: "", abn_acn: "",
  email: "", phone: "", postal_address: "",
};

function ClientForm({ value, onChange }) {
  const isCompany = value.client_type === "company";
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3 py-1">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Client type</label>
        <Select value={value.client_type} onValueChange={(v) => set("client_type", v)}>
          <SelectTrigger className="rounded-sm mt-1.5 h-9" data-testid="client-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="company">Company</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Display name *</label>
        <Input value={value.display_name} onChange={(e) => set("display_name", e.target.value)}
          placeholder={isCompany ? "e.g. Lusso Homes Pty Ltd" : "e.g. John & Mary Smith"}
          className="rounded-sm mt-1.5 h-9" data-testid="client-name-input" />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          {isCompany ? "Legal / entity name" : "Legal name (if different)"}
        </label>
        <Input value={value.legal_name} onChange={(e) => set("legal_name", e.target.value)}
          placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="client-legal-input" />
      </div>
      {isCompany && (
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">ABN / ACN</label>
          <Input value={value.abn_acn} onChange={(e) => set("abn_acn", e.target.value)}
            placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="client-abn-input" />
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Email</label>
          <Input value={value.email} onChange={(e) => set("email", e.target.value)}
            placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="client-email-input" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Phone</label>
          <Input value={value.phone} onChange={(e) => set("phone", e.target.value)}
            placeholder="Optional" className="rounded-sm mt-1.5 h-9" data-testid="client-phone-input" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Postal address</label>
        <Textarea value={value.postal_address} onChange={(e) => set("postal_address", e.target.value)}
          placeholder="Optional" className="rounded-sm mt-1.5 text-sm" data-testid="client-postal-input" />
      </div>
    </div>
  );
}

export default function ProjectClients({ projectId, project, canManage, onChange }) {
  const clients = project?.clients || [];
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // client_id or null (=new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [logoBusyId, setLogoBusyId] = useState(null);
  const logoInput = useRef(null);
  const logoTargetId = useRef(null);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.client_id);
    setForm({
      client_type: c.client_type || "individual", display_name: c.display_name || "",
      legal_name: c.legal_name || "", abn_acn: c.abn_acn || "", email: c.email || "",
      phone: c.phone || "", postal_address: c.postal_address || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.display_name.trim()) { toast.error("Display name is required"); return; }
    setSaving(true);
    try {
      let res;
      if (editing) {
        // editing a migrated client also clears its needs-review flag
        res = await api.patch(`/projects/${projectId}/clients/${editing}`, { ...form, needs_review: false });
        toast.success("Client updated");
      } else {
        res = await api.post(`/projects/${projectId}/clients`, form);
        toast.success("Client added");
      }
      onChange && onChange(res.data);
      setOpen(false);
    } catch (e) { toast.error(apiErr(e, "Failed to save client")); }
    finally { setSaving(false); }
  };

  const setPrimary = async (id) => {
    try {
      const res = await api.post(`/projects/${projectId}/clients/${id}/primary`);
      onChange && onChange(res.data);
      toast.success("Primary contact updated");
    } catch (e) { toast.error(apiErr(e, "Failed to set primary")); }
  };

  const remove = async (id) => {
    try {
      const res = await api.delete(`/projects/${projectId}/clients/${id}`);
      onChange && onChange(res.data);
      toast.success("Client removed");
    } catch (e) { toast.error(apiErr(e, "Failed to remove client")); }
  };

  const clearReview = async (id) => {
    try {
      const res = await api.patch(`/projects/${projectId}/clients/${id}`, { needs_review: false });
      onChange && onChange(res.data);
      toast.success("Marked as reviewed");
    } catch (e) { toast.error(apiErr(e, "Failed to update")); }
  };

  const pickLogo = (id) => { logoTargetId.current = id; logoInput.current?.click(); };
  const uploadLogo = async (file) => {
    const id = logoTargetId.current;
    if (!file || !id) return;
    setLogoBusyId(id);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/projects/${projectId}/clients/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.get(`/projects/${projectId}`);
      onChange && onChange(res.data);
      toast.success("Logo updated");
    } catch (e) { toast.error(apiErr(e, "Logo upload failed")); }
    finally { setLogoBusyId(null); }
  };

  return (
    <div data-testid="project-clients">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl font-bold">Clients</h3>
          <p className="text-sm text-zinc-500">{clients.length} client{clients.length !== 1 ? "s" : ""} on this project · the primary shows on cards &amp; reports</p>
        </div>
        {canManage && (
          <Button onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2" data-testid="add-client-button">
            <Plus className="h-4 w-4" /> Add Client
          </Button>
        )}
      </div>

      <input ref={logoInput} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
        onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ""; }} data-testid="client-logo-input" />

      {clients.length === 0 ? (
        <div className="border border-dashed border-zinc-300 bg-white p-10 text-center rounded-sm">
          <UserIcon className="h-7 w-7 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">{canManage ? "No clients yet — add the client(s) for this project." : "No clients recorded."}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {clients.map((c) => {
            const isCompany = c.client_type === "company";
            const Icon = isCompany ? Building2 : UserIcon;
            return (
              <div key={c.client_id} className="bg-white border border-zinc-200 rounded-sm p-4" data-testid={`client-row-${c.client_id}`}>
                <div className="flex items-start gap-3">
                  <div className="shrink-0">
                    {c.logo_path ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.logo_path}`} alt=""
                        className="h-12 w-12 rounded-sm object-contain border border-zinc-100 bg-white" />
                    ) : (
                      <div className="h-12 w-12 rounded-sm bg-zinc-100 flex items-center justify-center text-zinc-400">
                        <Icon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-bold truncate">{c.display_name}</p>
                      {c.primary && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-zinc-900 text-white px-1.5 py-0.5 rounded-sm" data-testid={`client-primary-badge-${c.client_id}`}>
                          <Star className="h-2.5 w-2.5 fill-white" /> Primary
                        </span>
                      )}
                      <span className="text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-200 rounded-sm px-1.5 py-0.5">{isCompany ? "Company" : "Individual"}</span>
                    </div>
                    {(c.legal_name || c.abn_acn) && (
                      <p className="text-xs text-zinc-500 mt-0.5 truncate">
                        {c.legal_name}{c.legal_name && c.abn_acn ? " · " : ""}{c.abn_acn ? `ABN/ACN ${c.abn_acn}` : ""}
                      </p>
                    )}
                    <div className="mt-2 space-y-1 text-sm text-zinc-600">
                      <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-zinc-400 shrink-0" />{c.email || <span className="text-zinc-400">No email</span>}</p>
                      <p className="flex items-center gap-2 truncate"><Phone className="h-3.5 w-3.5 text-zinc-400 shrink-0" />{c.phone || <span className="text-zinc-400">No phone</span>}</p>
                      {c.postal_address && <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-zinc-400 shrink-0 mt-0.5" />{c.postal_address}</p>}
                    </div>
                    {c.needs_review && (
                      <button onClick={() => clearReview(c.client_id)} disabled={!canManage}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded-sm px-2 py-0.5 hover:bg-amber-200 transition-colors disabled:cursor-default"
                        data-testid={`client-review-badge-${c.client_id}`} title={canManage ? "Click to mark as reviewed" : ""}>
                        <AlertTriangle className="h-3 w-3" /> Imported · needs review
                      </button>
                    )}
                  </div>
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="text-zinc-400 hover:text-zinc-900 transition-colors shrink-0" data-testid={`client-menu-${c.client_id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-sm">
                        {!c.primary && (
                          <DropdownMenuItem onClick={() => setPrimary(c.client_id)} data-testid={`client-set-primary-${c.client_id}`}>
                            <Star className="h-3.5 w-3.5 mr-2" /> Make primary
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openEdit(c)} data-testid={`client-edit-${c.client_id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => pickLogo(c.client_id)} disabled={logoBusyId === c.client_id}>
                          <Upload className="h-3.5 w-3.5 mr-2" /> {logoBusyId === c.client_id ? "Uploading…" : "Upload logo"}
                        </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-red-600 hover:bg-red-50" data-testid={`client-delete-${c.client_id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove
                            </div>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-sm">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove &ldquo;{c.display_name}&rdquo;?</AlertDialogTitle>
                              <AlertDialogDescription>This only removes the client from this project.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                              <AlertDialogAction className="rounded-sm bg-red-600 hover:bg-red-700" onClick={() => remove(c.client_id)}>Remove</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit client" : "Add client"}</DialogTitle>
            <DialogDescription>Details for a client on this project. Only the display name is required.</DialogDescription>
          </DialogHeader>
          <ClientForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="save-client-button">
              {saving ? "Saving…" : editing ? "Save changes" : "Add client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
